import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  Brain,
  ArrowLeft,
  Send,
  Loader2,
  Sparkles,
  BookOpen,
  Lightbulb,
  MessageCircle,
  GraduationCap,
  RefreshCw,
  Beaker,
  ChevronRight,
  Heart,
  Target,
  CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { toast } from 'sonner';

// Rotating emotional tones for MinnowRoom - supportive and encouraging
const MINNOW_TONES = [
  "Every great founder started exactly where you are now.",
  "Questions are the beginning of wisdom. Ask freely.",
  "Your unique perspective is your greatest asset.",
  "Progress, not perfection. Let's take the next step together.",
  "Curiosity is the seed of every successful startup."
];

const MinnowRoom = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [emotionalTone] = useState(() => MINNOW_TONES[Math.floor(Math.random() * MINNOW_TONES.length)]);
  const [messages, setMessages] = useState([
    {
      type: 'ai',
      content: `Hi ${user?.name?.split(' ')[0] || 'there'}! I'm Minnow, your adaptive learning companion. I'm here to help you think through problems and learn at your own pace. What's on your mind today?`
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestedPrompts, setSuggestedPrompts] = useState([]);
  const [loadingPrompts, setLoadingPrompts] = useState(false);
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    loadPrompts();
  }, []);

  const loadPrompts = async () => {
    setLoadingPrompts(true);
    try {
      const response = await api.getMinnowPrompts();
      setSuggestedPrompts(response.data.prompts || []);
    } catch (error) {
      // Fallback prompts
      setSuggestedPrompts([
        { text: "How do I validate my startup idea?", category: "idea" },
        { text: "Explain product-market fit simply", category: "learning" },
        { text: "How do I approach my first customers?", category: "growth" }
      ]);
    } finally {
      setLoadingPrompts(false);
    }
  };

  const getPromptIcon = (category) => {
    switch (category) {
      case 'idea': return Lightbulb;
      case 'learning': return BookOpen;
      case 'growth': return MessageCircle;
      case 'funding': return GraduationCap;
      case 'confidence':
      case 'emotional': return Sparkles;
      default: return MessageCircle;
    }
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { type: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await api.minnowChat(userMessage);
      
      // Check if there's curriculum steering
      const steering = response.data.steering;
      // Check if there's Minnow Insights (adaptive curriculum)
      const insights = response.data.insights;
      
      let messageObj = { type: 'ai', content: response.data.response };
      
      if (steering && steering.detected) {
        messageObj.steering = steering;
      }
      
      if (insights && insights.detected) {
        messageObj.insights = insights;
        // Show toast if weekly focus was updated
        if (insights.focus_updated) {
          toast.success('Weekly Focus updated based on your conversation', {
            description: 'Check your dashboard to see your personalized learning path.',
            duration: 5000
          });
        }
      }
      
      setMessages(prev => [...prev, messageObj]);
    } catch (error) {
      toast.error('Could not get response');
      setMessages(prev => [...prev, { 
        type: 'ai', 
        content: "I'm having trouble connecting right now. Please try again in a moment." 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handlePromptClick = (promptText) => {
    setInput(promptText);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-ocean-blue text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/dashboard')}
                className="text-white hover:bg-white/10"
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                  <Brain className="w-6 h-6" />
                </div>
                <div>
                  <h1 className="font-bold">{t('minnow.title')}</h1>
                  <p className="text-xs text-white/70">{t('minnow.subtitle')}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-mint animate-pulse" />
              <span className="text-sm text-white/70 hidden sm:inline">{t('minnow.personalizedForYou')}</span>
            </div>
          </div>
        </div>
        
        {/* Emotional Tone Header */}
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/10 backdrop-blur-sm border-t border-white/10"
          data-testid="emotional-tone-header"
        >
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-2.5">
            <div className="flex items-center gap-2">
              <Heart className="w-4 h-4 text-coral flex-shrink-0" />
              <p className="text-sm text-white/90 italic">
                "{emotionalTone}"
              </p>
            </div>
          </div>
        </motion.div>
        
        {/* Wave divider */}
        <div className="wave-divider h-8" />
      </header>

      {/* Chat Area */}
      <ScrollArea className="flex-1 p-4" ref={scrollRef}>
        <div className="max-w-4xl mx-auto space-y-6 pb-4">
          {messages.map((message, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`flex gap-3 max-w-[85%] ${message.type === 'user' ? 'flex-row-reverse' : ''}`}>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  message.type === 'user' 
                    ? 'gradient-ocean text-white' 
                    : 'bg-mint text-ocean-blue'
                }`}>
                  {message.type === 'user' ? (
                    <span className="text-sm font-medium">{user?.name?.charAt(0).toUpperCase()}</span>
                  ) : (
                    <Brain className="w-5 h-5" />
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <div className={`p-4 rounded-2xl ${
                    message.type === 'user'
                      ? 'gradient-ocean text-white rounded-tr-sm'
                      : 'bg-card border border-border shadow-sm rounded-tl-sm'
                  }`} data-testid={`message-${message.type}-${index}`}>
                    <p className="whitespace-pre-wrap leading-relaxed">{message.content}</p>
                  </div>
                  
                  {/* Curriculum Steering Suggestions */}
                  {message.steering && message.type === 'ai' && (
                    <div className="flex flex-wrap gap-2 mt-1">
                      {message.steering.module_title && (
                        <Link to={`/curriculum/${message.steering.module}`}>
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-full border-ocean-blue/30 hover:border-ocean-blue hover:bg-ocean-blue/5"
                          >
                            <BookOpen className="w-4 h-4 mr-2 text-ocean-blue" />
                            {message.steering.module_title}
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        </Link>
                      )}
                      {message.steering.lab_title && (
                        <Link to="/labs">
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-full border-coral/30 hover:border-coral hover:bg-coral/5"
                          >
                            <Beaker className="w-4 h-4 mr-2 text-coral" />
                            {message.steering.lab_title}
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        </Link>
                      )}
                    </div>
                  )}
                  
                  {/* Minnow Insights - Adaptive Focus Update */}
                  {message.insights && message.insights.detected && message.type === 'ai' && (
                    <motion.div
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-2 p-3 rounded-xl bg-mint/20 border border-mint/40"
                    >
                      <div className="flex items-start gap-2">
                        <div className="w-6 h-6 rounded-full bg-mint flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Target className="w-3.5 h-3.5 text-ocean-blue" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-ocean-blue">{t('minnow.weeklyFocusUpdated')}</span>
                            {message.insights.focus_updated && (
                              <Badge variant="outline" className="text-xs bg-mint/30 border-mint text-ocean-blue">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                {t('minnow.personalized')}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">{message.insights.reason}</p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {message.insights.recommended_module && (
                              <Link to={`/curriculum/${message.insights.recommended_module}`}>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 text-xs rounded-full border-ocean-blue/30 hover:border-ocean-blue"
                                >
                                  <BookOpen className="w-3 h-3 mr-1" />
                                  {t('minnow.goToModule')}
                                </Button>
                              </Link>
                            )}
                            {message.insights.recommended_lab && (
                              <Link to={`/lab/${message.insights.recommended_lab}`}>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 text-xs rounded-full border-coral/30 hover:border-coral"
                                >
                                  <Beaker className="w-3 h-3 mr-1" />
                                  {t('minnow.startLab')}
                                </Button>
                              </Link>
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}

          {loading && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex justify-start"
            >
              <div className="flex gap-3 max-w-[85%]">
                <div className="w-10 h-10 rounded-xl bg-mint flex items-center justify-center">
                  <Brain className="w-5 h-5 text-ocean-blue" />
                </div>
                <div className="p-4 rounded-2xl bg-card border border-border shadow-sm rounded-tl-sm">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-ocean-blue" />
                    <span className="text-muted-foreground">{t('common.thinking')}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Suggested Prompts */}
          {messages.length <= 2 && !loading && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="pt-4"
            >
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-muted-foreground">Personalized for you:</p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={loadPrompts}
                  disabled={loadingPrompts}
                  className="text-ocean-blue hover:text-ocean-blue/80"
                  data-testid="refresh-prompts-btn"
                >
                  <RefreshCw className={`w-4 h-4 mr-1 ${loadingPrompts ? 'animate-spin' : ''}`} />
                  {t('minnow.newPrompts')}
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {suggestedPrompts.map((prompt, index) => {
                  const Icon = getPromptIcon(prompt.category);
                  return (
                    <Button
                      key={index}
                      variant="outline"
                      size="sm"
                      onClick={() => handlePromptClick(prompt.text)}
                      className="rounded-full border-ocean-blue/30 hover:border-ocean-blue hover:bg-ocean-blue/5"
                      data-testid={`suggested-prompt-${index}`}
                    >
                      <Icon className="w-4 h-4 mr-2 text-ocean-blue" />
                      {prompt.text}
                    </Button>
                  );
                })}
              </div>
            </motion.div>
          )}
        </div>
      </ScrollArea>

      {/* Input Area */}
      <div className="sticky bottom-0 bg-background border-t border-border p-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('minnow.askAnything')}
                className="min-h-[56px] max-h-[200px] pr-14 rounded-2xl resize-none bg-card border-border"
                disabled={loading}
                data-testid="minnow-input"
              />
              <Button
                size="icon"
                onClick={handleSend}
                disabled={!input.trim() || loading}
                className="absolute right-2 bottom-2 rounded-xl h-10 w-10 gradient-ocean text-white"
                data-testid="minnow-send-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground text-center mt-2">
            {t('minnow.enterHint')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default MinnowRoom;
